/**
 * Cache Manager - Simple localStorage-based cache with TTL
 * Provides consistent cache management across services
 */

const CACHE_PREFIX = 'app_cache_'
const RECORD_PREFIX = 'app_record_'

/**
 * Get a list from cache if it hasn't expired
 * @param {string} key - Cache key
 * @returns {any} Cached value or null if expired/missing
 */
export const cacheGetList = (key) => {
  try {
    const cacheKey = CACHE_PREFIX + key
    const cached = localStorage.getItem(cacheKey)

    if (!cached) {
      return null
    }

    const { data, expiresAt } = JSON.parse(cached)

    if (expiresAt && new Date(expiresAt) < new Date()) {
      localStorage.removeItem(cacheKey)
      return null
    }

    return data
  } catch (error) {
    console.debug('[Cache Manager] Failed to get cache:', error.message)
    return null
  }
}

/**
 * Set a list in cache with optional TTL
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} hoursToCache - Hours until expiry (optional, default 1 hour)
 */
export const cacheSetList = (key, data, hoursToCache = 1) => {
  try {
    const cacheKey = CACHE_PREFIX + key
    const expiresAt = new Date(Date.now() + hoursToCache * 60 * 60 * 1000).toISOString()

    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        expiresAt,
      }),
    )
  } catch (error) {
    console.debug('[Cache Manager] Failed to set cache:', error.message)
  }
}

/**
 * Get a single record from cache if it hasn't expired
 * @param {string} namespace - Cache namespace (e.g., 'customers', 'orders')
 * @param {number|string} id - Record ID
 * @returns {any} Cached record or null if expired/missing
 */
export const cacheGetRecord = (namespace, id) => {
  try {
    const cacheKey = RECORD_PREFIX + namespace + '_' + id
    const cached = localStorage.getItem(cacheKey)

    if (!cached) {
      return null
    }

    const { data, expiresAt } = JSON.parse(cached)

    if (expiresAt && new Date(expiresAt) < new Date()) {
      localStorage.removeItem(cacheKey)
      return null
    }

    return data
  } catch (error) {
    console.debug('[Cache Manager] Failed to get record cache:', error.message)
    return null
  }
}

/**
 * Set a single record in cache with optional TTL
 * @param {string} namespace - Cache namespace (e.g., 'customers', 'orders')
 * @param {number|string} id - Record ID
 * @param {any} data - Data to cache
 * @param {number} hoursToCache - Hours until expiry (optional, default 1 hour)
 */
export const cacheSetRecord = (namespace, id, data, hoursToCache = 1) => {
  try {
    const cacheKey = RECORD_PREFIX + namespace + '_' + id
    const expiresAt = new Date(Date.now() + hoursToCache * 60 * 60 * 1000).toISOString()

    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        expiresAt,
      }),
    )
  } catch (error) {
    console.debug('[Cache Manager] Failed to set record cache:', error.message)
  }
}

/**
 * Remove an item from cache
 * @param {string} key - Cache key
 */
export const cacheRemove = (key) => {
  try {
    const cacheKey = CACHE_PREFIX + key
    localStorage.removeItem(cacheKey)
  } catch (error) {
    console.debug('[Cache Manager] Failed to remove cache:', error.message)
  }
}

/**
 * Clear all app cache for current user
 */
export const cacheClearForUser = () => {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach((key) => {
      if (key.startsWith(CACHE_PREFIX) || key.startsWith(RECORD_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.debug('[Cache Manager] Failed to clear user cache:', error.message)
  }
}

/**
 * Clear all app cache (global)
 */
export const cacheRemoveAll = () => {
  cacheClearForUser()
}
